/**
 * Unified OAuth Error Types
 *
 * Single source of truth for OAuth errors across frontend and backend.
 * Replaces OAuthProviderError (document-specific) with universal OAuthError.
 */

/**
 * OAuth error type enum
 */
export enum OAuthErrorType {
  INSUFFICIENT_SCOPE = "INSUFFICIENT_SCOPE",
  TOKEN_EXPIRED = "TOKEN_EXPIRED",
  CONNECTION_NOT_FOUND = "CONNECTION_NOT_FOUND",
  INVALID_TOKEN = "INVALID_TOKEN",
  NETWORK = "NETWORK",
  RATE_LIMIT = "RATE_LIMIT",
  SERVICE_ERROR = "SERVICE_ERROR",
  /**
   * The provider account/tenant is misconfigured on the provider's side
   * (e.g. SharePoint Online not licensed, a feature not enabled by the
   * org admin). Not a Nexus fault and not fixable by reconnecting — the
   * user must contact their provider/workspace administrator.
   */
  PROVIDER_CONFIG = "PROVIDER_CONFIG",
  UNKNOWN = "UNKNOWN"
}

/**
 * OAuth error discriminated union
 *
 * Use canReconnect to determine if "Reconnect" button should be shown.
 */
export type OAuthError =
  | {
      type: OAuthErrorType.INSUFFICIENT_SCOPE;
      message: string;
      canReconnect: true;
      requiredScopes?: string[];
      grantedScopes?: string[];
    }
  | {
      type: OAuthErrorType.TOKEN_EXPIRED;
      message: string;
      canReconnect: true;
    }
  | {
      type: OAuthErrorType.CONNECTION_NOT_FOUND;
      message: string;
      canReconnect: true;
    }
  | {
      type: OAuthErrorType.INVALID_TOKEN;
      message: string;
      canReconnect: true;
    }
  | {
      type: OAuthErrorType.NETWORK;
      message: string;
      canReconnect: false;
      statusCode?: number;
    }
  | {
      type: OAuthErrorType.RATE_LIMIT;
      message: string;
      canReconnect: false;
      retryAfter?: number;
    }
  | {
      type: OAuthErrorType.SERVICE_ERROR;
      message: string;
      canReconnect: false;
      serviceMessage?: string;
    }
  | {
      type: OAuthErrorType.PROVIDER_CONFIG;
      message: string;
      canReconnect: false;
      /** Raw provider/technical detail, shown muted for support context. */
      serviceMessage?: string;
    }
  | {
      type: OAuthErrorType.UNKNOWN;
      message: string;
      canReconnect: false;
    };

/**
 * OAuth connection state (used by hooks/adapters)
 */
export interface OAuthConnectionState {
  connectionId: string | null;
  isConnected: boolean;
  isHealthy: boolean;
  needsReauth: boolean;
  error: OAuthError | null;
}

/**
 * OAuth reconnect handler signature
 */
export type OAuthReconnectHandler = (service: string, connectionId?: string) => void;
